import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileChips, formatDate, projectInfo, DeadlineWarning } from "@/components/shared";
import { MessageThread } from "@/components/ProjectMessages";
import { ArrowLeft, Award, Star } from "lucide-react";import { toast } from "sonner";

const statusMeta = {
  pending: { label: "Değerlendirmede", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  accepted: { label: "Kabul edildi", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  rejected: { label: "Reddedildi", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
};

export default function CustomerProjectDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [rates, setRates] = useState(null);
  const [preferring, setPreferring] = useState(null);

  const load = useCallback(() => {
    api.get(`/customer/projects/${id}`).then((r) => setP(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  }, [id, load]);

  const prefer = async (offerId) => {
    setPreferring(offerId);
    try {
      await api.post(`/customer/offers/${offerId}/prefer`);
      toast.success("Tercihiniz kaydedildi ve ekibimize iletildi");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPreferring(null);
    }
  };

  const toTRY = useCallback(
    (o) => {
      if (o.currency === "TRY") return o.price;
      const rate = rates?.[o.currency];
      return rate ? o.price * rate : null;
    },
    [rates]
  );

  if (!p) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const offers = p.request?.offers || [];
  let bestId = null;
  if (offers.length > 1) {
    const vals = offers.map((o) => toTRY(o));
    if (vals.every((v) => v !== null)) {
      bestId = offers[vals.indexOf(Math.min(...vals))].id;
    } else if (new Set(offers.map((o) => o.currency)).size === 1) {
      bestId = offers.reduce((a, b) => (a.price <= b.price ? a : b)).id;
    }
  }
  const fmtTL = (v) => v.toLocaleString("tr-TR", { maximumFractionDigits: 0 });
  const winner = offers.find((o) => o.status === "accepted");

  return (
    <div className="space-y-8 fade-in">
      <div>
        <Link to="/musteri/projeler" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="back-to-customer-projects">
          <ArrowLeft size={15} /> Projelerime Dön
        </Link>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Proje Detayı</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="customer-project-title">{p.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
            <span data-testid="customer-project-info">{projectInfo(p)}</span>
            {p.request && (
              <>
                <span>Son teklif tarihi: {formatDate(p.request.deadline)}</span>
                <DeadlineWarning deadline={p.request.deadline} time={p.request.deadline_time} />
              </>
            )}
          </div>
        </div>
      </div>

      {winner && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-5 max-w-3xl flex items-start gap-4" data-testid="winner-banner">
          <div className="bg-emerald-600 text-white p-2.5 shrink-0"><Award size={18} /></div>
          <div>
            <div className="font-heading font-bold text-lg tracking-tight text-emerald-700">Projeniz Onaylandı</div>
            <p className="text-sm text-slate-600 mt-1">
              Kazanan firma: <strong>{winner.company_name}</strong> — {winner.price.toLocaleString("tr-TR")} {winner.currency}.
              Sözleşme ve üretim süreci için ekibimiz sizinle iletişime geçecek.
            </p>
          </div>
        </div>
      )}

      {p.description && (
        <div className="border border-slate-200 bg-white p-5 max-w-3xl space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Proje Açıklaması</div>
          <p className="text-sm text-slate-600 whitespace-pre-line">{p.description}</p>
        </div>
      )}
      {p.files?.length > 0 && (
        <div className="border border-slate-200 bg-white p-5 max-w-3xl space-y-3">
          <div className="text-xs uppercase tracking-wider text-slate-500">Proje Dosyaları</div>
          <FileChips files={p.files} />
        </div>
      )}
      {p.request?.files?.length > 0 && (
        <div className="border border-slate-200 bg-white p-5 max-w-3xl space-y-3" data-testid="customer-request-files">
          <div className="text-xs uppercase tracking-wider text-slate-500">Talep Ekleri</div>
          <FileChips files={p.request.files} />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Gelen Teklifler</h2>
          {p.request && (
            <Link to={`/musteri/teklifler/${p.request.id}`} data-testid="open-compare-link" className="text-sm font-semibold text-slate-900 underline underline-offset-4 hover:text-slate-600 transition-colors duration-200">
              Karşılaştırma Sayfasını Aç
            </Link>
          )}
        </div>
        {!p.request ? (
          <div className="border border-slate-200 bg-white p-8 mt-4 max-w-3xl" data-testid="no-request-panel">
            <p className="text-sm text-slate-500">
              Projeniz ekibimiz tarafından inceleniyor. Teklif süreci başlatıldığında tedarikçilerden gelen
              fiyatları burada karşılaştırabileceksiniz.
            </p>
          </div>
        ) : offers.length === 0 ? (
          <div className="border border-slate-200 bg-white p-8 mt-4 max-w-3xl" data-testid="no-offers-panel">
            <p className="text-sm text-slate-500">
              Talebiniz {p.request.supplier_count} tedarikçiye iletildi, henüz teklif gelmedi. Teklifler geldikçe burada görünecek.
            </p>
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-500 mt-2" data-testid="customer-rates-info">
              {rates?.USD ? <>Güncel kur: 1 USD ≈ {rates.USD.toLocaleString("tr-TR")} TL · 1 EUR ≈ {rates.EUR.toLocaleString("tr-TR")} TL</> : ""}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {offers.map((o) => {
                const tl = toTRY(o);
                const isBest = o.id === bestId;
                const meta = statusMeta[o.status] || statusMeta.pending;
                return (
                  <div
                    key={o.id}
                    data-testid={`customer-offer-card-${o.id}`}
                    className={`relative border p-5 bg-white space-y-3 ${o.status === "accepted" ? "border-emerald-500/60" : isBest ? "border-emerald-500/50" : "border-slate-200"}`}
                  >
                    {isBest && (
                      <div className="absolute -top-3 left-4 flex items-center gap-1.5 bg-emerald-600 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1">
                        <Award size={11} /> En İyi Teklif
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading font-bold text-lg tracking-tight leading-tight">{o.company_name}</div>
                      <div className="flex flex-col items-end gap-1">
                        {o.status !== "pending" && (
                          <Badge variant="outline" className={`rounded-none text-xs ${meta.cls}`}>{meta.label}</Badge>
                        )}
                        {o.customer_preferred && (
                          <Badge className="rounded-none text-[10px] bg-blue-600 text-white gap-1" data-testid={`preferred-badge-${o.id}`}>
                            <Star size={10} /> Tercihiniz
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className={`font-heading font-extrabold text-3xl tracking-tight ${isBest ? "text-emerald-600" : "text-slate-900"}`}>
                        {o.price.toLocaleString("tr-TR")} <span className="text-sm font-medium text-slate-500">{o.currency}</span>
                      </div>
                      {o.currency !== "TRY" && tl !== null && (
                        <div className="text-xs text-slate-500 mt-1">≈ {fmtTL(tl)} TL</div>
                      )}
                    </div>
                    {o.description && <p className="text-xs text-slate-500 line-clamp-3 whitespace-pre-line">{o.description}</p>}
                    <div className="text-xs text-slate-400">Teklif tarihi: {formatDate(o.created_at)}</div>
                    {o.status === "pending" && !o.customer_preferred && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => prefer(o.id)}
                        disabled={preferring === o.id}
                        data-testid={`prefer-offer-${o.id}`}
                        className="w-full rounded-none border-blue-500/30 text-blue-600 hover:bg-blue-500/10 gap-1.5 transition-colors duration-200"
                      >
                        <Star size={13} /> {preferring === o.id ? "Kaydediliyor..." : "Bu Teklifi Tercih Ederim"}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="max-w-3xl">
        <MessageThread endpoint={`/customer/projects/${id}/messages`} myRole="customer" title="Ekibimize Sorun" emptyText="Projenizle ilgili sorularınızı buradan yazabilirsiniz; ekibimiz panelden yanıtlar." placeholder="Sorunuzu veya notunuzu yazın..." />
      </div>
    </div>
  );
}
